import { z } from "zod";
import { STAFF_ROLES } from "Constant/Constant";

export const staffUserSchema = (isEdit) =>
  z
    .object({
      fullName: z.string().trim().min(1, "Full name is required"),

      email: z.string().trim().min(1, "Email is required").email("Invalid email format"),

      pin: z
        .string()
        .min(1, "PIN is required")
        .regex(/^\d{4}$/, "PIN must be exactly 4 digits"),

      role: z
        .string()
        .min(1, "Role is required")
        .refine((val) => Object.values(STAFF_ROLES).includes(val), {
          message: "Invalid role selected",
        }),

      username: z
        .string()
        .trim()
        .min(1, "Username is required")
        .regex(/^\S+$/, "Username must be a single word with no spaces"),

      password: isEdit
        ? z.string().trim().optional()
        : z.string().trim().min(1, "Password is required"),

      // raw digits only (prefix handled separately in form state)
      phoneNumber: z.string().trim().min(1, "Phone number is required"),

      siteid: z.string().trim().optional(),

      notificationToken: z.string().trim().optional(),

      playerId: z.string().trim().optional(),
    })
    .passthrough()
    .superRefine((data, ctx) => {
      if (data.password) {
        const pass = data.password;

        if (pass.length < 8) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Password must be at least 8 characters long",
            path: ["password"],
          });
        }

        if (!/[A-Z]/.test(pass)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At least one uppercase letter required",
            path: ["password"],
          });
        }

        if (!/[a-z]/.test(pass)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At least one lowercase letter required",
            path: ["password"],
          });
        }

        if (!/[0-9]/.test(pass)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At least one number required",
            path: ["password"],
          });
        }

        if (!/[^A-Za-z0-9]/.test(pass)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At least one special character required",
            path: ["password"],
          });
        }

        if (/\s/.test(pass)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Password must not contain spaces",
            path: ["password"],
          });
        }
      }

      if (data.phoneNumber) {
        const digits = data.phoneNumber;

        if (digits.length < 8) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Mobile number must be at least 8 digits",
            path: ["phoneNumber"],
          });
        }

        if (digits.length > 12) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Mobile number must be under 12 digits",
            path: ["phoneNumber"],
          });
        }

        if (!/^\d+$/.test(digits)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Mobile number must only contain digits",
            path: ["phoneNumber"],
          });
        }
      }

      if (data.role !== STAFF_ROLES.STAFF_SUPER_ADMIN && !data.siteid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Site is required",
          path: ["siteid"],
        });
      }
    });

export const parseStaffUserErrors = (data, isEdit) => {
  const result = staffUserSchema(isEdit).safeParse(data);

  if (result.success) return {};

  const errors = {};

  for (const issue of result.error.issues) {
    const key = issue.path.join(".");
    if (!errors[key]) {
      errors[key] = issue.message;
    }
  }

  return errors;
};
